import { rejectInvalidSyncKey } from 'serviceWorker/services/factories/invalidSyncKey';
import { HustroDbEntityTablesUnion } from 'serviceWorker/db';
import {
  defaultConfig,
  generateHeaders,
  makeNormalizeResponse,
} from '../../helpers/httpRequests';
import {
  HeadersData,
  NormalizedEntityResponseData,
} from '../factories/types';
import { pull } from '../pull';
import { FieldValueType, FIELD_VALUES_CONFIG } from './fieldValuesConfig';

function shouldSkip(data: HeadersData, signal: AbortSignal): boolean {
  return !data.api || !data.projectId || signal.aborted;
}

export function createFieldValueFetcher<T>(
  fieldValueType: FieldValueType
): {
  fetchFieldValues: (
    data: HeadersData,
    controller: AbortController
  ) => Promise<NormalizedEntityResponseData<T>> | undefined;
  fetchUpdatedFieldValues: (
    data: HeadersData,
    syncKey: string,
    controller: AbortController
  ) => Promise<NormalizedEntityResponseData<T>> | undefined;
} {
  const config = FIELD_VALUES_CONFIG[fieldValueType];
  const parseResponse = makeNormalizeResponse<T>(config.responseType);

  const fetchFieldValues = (
    data: HeadersData,
    controller: AbortController
  ): Promise<NormalizedEntityResponseData<T>> | undefined => {
    const { signal } = controller;
    if (shouldSkip(data, signal)) {
      return;
    }
    const builtUrl = `${data.api}/v2/project/${data.projectId}/${config.endpoint}`;

    return pull(builtUrl, {
      ...defaultConfig,
      method: 'GET',
      headers: generateHeaders(data),
      signal,
    }).then(parseResponse);
  };

  const fetchUpdatedFieldValues = (
    data: HeadersData,
    syncKey: string,
    controller: AbortController
  ): Promise<NormalizedEntityResponseData<T>> | undefined => {
    const { signal } = controller;
    if (!data.api || !data.projectId) {
      //not enough data, skip.
      return;
    }
    const builtUrl = `${data.api}/v2/project/${
      data.projectId
    }/${config.endpoint}?syncKey=${encodeURIComponent(syncKey)}`;

    return pull(builtUrl, {
      ...defaultConfig,
      method: 'GET',
      headers: generateHeaders(data),
      signal,
    })
      .then((response: Response) => rejectInvalidSyncKey(response, config.tableName as HustroDbEntityTablesUnion))
      .then(parseResponse);
  };

  return {
    fetchFieldValues,
    fetchUpdatedFieldValues,
  };
}
